import { useState, useEffect } from "react";
import { C } from "./re-colors.ts";
import { BANK, BRIEFINGS, SUBBAR_CONFIG, EXAM_CATEGORIES, PEER_DATA, _auditLog, _tasks, EXCEPTIONS, LENDING_PIPELINE, FRAUD_DISPUTES, IT_TICKETS, BCP_DATA, WATCHLIST_CREDITS, MORTGAGE_PIPELINE, WEALTH_DATA, INVESTMENT_PORTFOLIO, CD_MATURITY_LADDER, LIQUIDITY_METRICS, BRANCH_DATA, TEST_DATA, INTRADAY_POSITION, VENDOR_REGISTRY, LOAN_PRICING_ASSUMPTIONS } from "./re-data.ts";
import type { Exception, PipelineItem } from "./re-data.ts";
import { Bar, Bdg, Card, KpiCard, PH, SourcePill } from "./re-components.tsx";
import { DocModal } from "./re-modals.tsx";

// ── TASK QUEUE ────────────────────────────────────────────────────
export function TaskQueue({
  pid, persona, onLog,
}: {
  pid: string;
  persona: { name: string };
  onLog: (action: string, detail: string, category: string) => void;
}) {
  const [tasks, setTasks] = useState([..._tasks[pid]]);
  const [filter, setFilter] = useState("all");
  const prioColor: Record<string, string> = { critical: C.burgundy, high: C.gold, medium: C.rust, low: C.forest };

  function toggle(id: number) {
    setTasks(t => t.map(x => x.id === id ? { ...x, done: !x.done } : x));
    const t = tasks.find(x => x.id === id);
    if (t) onLog("Completed task", t.title, "Tasks");
  }

  const cats = [...new Set(tasks.map(t => t.category))];
  const filtered = filter === "all" ? tasks : tasks.filter(t => t.category === filter);
  const done = tasks.filter(t => t.done).length;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: C.text }}>My Task Queue</div>
          <div style={{ fontSize: 11, color: C.muted }}>{done} of {tasks.length} complete · {persona.name}</div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["all", ...cats].map(c => (
            <button key={c} onClick={() => setFilter(c)} style={{ fontSize: 10, padding: "4px 10px", borderRadius: 20, border: `1px solid ${filter === c ? C.burgundy : C.border}`, background: filter === c ? C.burgundyBg : "transparent", color: filter === c ? C.burgundy : C.muted, cursor: "pointer", fontWeight: filter === c ? 700 : 400, textTransform: "capitalize" }}>
              {c}
            </button>
          ))}
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <Bar val={done} max={tasks.length} color={C.forest} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {filtered
          .sort((a, b) => ({ critical: 0, high: 1, medium: 2, low: 3 } as any)[a.priority] - ({ critical: 0, high: 1, medium: 2, low: 3 } as any)[b.priority])
          .map(t => (
            <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: t.done ? C.surface : C.card, border: `1px solid ${t.done ? C.border : prioColor[t.priority] + "30"}`, borderRadius: 10, opacity: t.done ? .6 : 1 }}>
              <button onClick={() => toggle(t.id)} style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${t.done ? C.forest : prioColor[t.priority]}`, background: t.done ? C.forest : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {t.done && <span style={{ color: "#fff", fontSize: 11 }}>✓</span>}
              </button>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: t.done ? 400 : 600, color: t.done ? C.muted : C.text, textDecoration: t.done ? "line-through" : "none" }}>{t.title}</div>
                <div style={{ display: "flex", gap: 8, marginTop: 3 }}>
                  <span style={{ fontSize: 10, color: C.dim }}>{t.category}</span>
                  <span style={{ fontSize: 10, color: prioColor[t.priority], fontWeight: 700 }}>Due: {t.due}</span>
                </div>
              </div>
              <Bdg label={t.priority} color={prioColor[t.priority]} />
            </div>
          ))}
      </div>
    </div>
  );
}

// ── SCENARIO MODELER ──────────────────────────────────────────────
export function ScenarioModeler() {
  const [rateShift, setRateShift] = useState(0);
  const [depositExit, setDepositExit] = useState(0);
  const [creGrowth, setCreGrowth] = useState(0);
  const [loading, setLoading] = useState(false);
  const [aiNarrative, setAiNarrative] = useState("");

  const baseNII = 38400000;
  const baseDeposits = 3620000000;
  const niiImpact = rateShift * 42000;
  const depositImpact = depositExit * 1000000;
  const newLDRatio = (((2872000000 + creGrowth * 1000000) / (baseDeposits - depositImpact)) * 100).toFixed(1);
  const newCreConc = (287 + creGrowth * 0.3).toFixed(0);
  const newNII = ((baseNII + niiImpact) / 1000000).toFixed(1);

  const sliders = [
    { label: `Rate Shift: ${rateShift > 0 ? "+" : ""}${rateShift} bps`, val: rateShift, set: setRateShift, min: -200, max: 200, step: 25 },
    { label: `Deposit Exit: $${depositExit}M`,                           val: depositExit, set: setDepositExit, min: 0, max: 400, step: 10 },
    { label: `New CRE Growth: $${creGrowth}M`,                          val: creGrowth, set: setCreGrowth, min: 0, max: 100, step: 5 },
  ];

  async function analyze() {
    setLoading(true);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 400,
          system: "You are a banking risk analyst. Provide a concise 3-bullet scenario analysis. Use specific numbers.",
          messages: [{ role: "user", content: `Analyze for Demo Regional Bank ($4.21B): Rate shift: ${rateShift}bps, Deposit exit: $${depositExit}M, CRE growth: $${creGrowth}M. Results: NII $${newNII}M (base $38.4M), L/D ${newLDRatio}%, CRE conc. ${newCreConc}%. Provide 3 bullets on risk/opportunity.` }],
        }),
      });
      const d = await res.json();
      setAiNarrative(d.content?.find((b: any) => b.type === "text")?.text || "");
    } catch {
      setAiNarrative("Connection error.");
    }
    setLoading(false);
  }

  const results = [
    { label: "Projected NII",      val: `$${newNII}M`,                                         base: "$38.4M", color: parseFloat(newNII) >= 38.4 ? C.forest : C.burgundy },
    { label: "L/D Ratio",          val: `${newLDRatio}%`,                                       base: "79.3%",  color: parseFloat(newLDRatio) < 85 ? C.forest : C.burgundy },
    { label: "CRE Concentration",  val: `${newCreConc}%`,                                       base: "287%",   color: parseInt(newCreConc) < 300 ? C.gold : C.burgundy    },
    { label: "Deposit Base",       val: `$${((baseDeposits - depositImpact) / 1e9).toFixed(2)}B`, base: "$3.62B", color: depositExit > 200 ? C.burgundy : C.gold            },
  ];

  return (
    <div>
      <PH title="Scenario Modeler" sub="Interactive what-if analysis" accentColor={C.purple} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 16 }}>Adjust Scenarios</div>
          {sliders.map(({ label, val, set, min, max, step }, i) => (
            <div key={i} style={{ marginBottom: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: C.muted }}>{label}</span>
                <button onClick={() => set(0)} style={{ fontSize: 10, color: C.dim, background: "none", border: `1px solid ${C.border}`, borderRadius: 10, padding: "1px 7px", cursor: "pointer" }}>Reset</button>
              </div>
              <input type="range" min={min} max={max} step={step} value={val} onChange={e => set(Number(e.target.value))} style={{ width: "100%", accentColor: C.burgundy }} />
            </div>
          ))}
          <button onClick={analyze} style={{ width: "100%", background: C.aiPurple, color: "#fff", border: "none", borderRadius: 10, padding: "11px", fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            {loading
              ? [0, 1, 2].map(i => <div key={i} style={{ width: 5, height: 5, borderRadius: "50%", background: "#fff", animation: `dot .9s ${i * .2}s infinite` }} />)
              : "✦ AI Analysis"}
          </button>
        </Card>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {results.map(({ label, val, base, color }, i) => (
              <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: .6, marginBottom: 6 }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color }}>{val}</div>
                <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>Base: {base}</div>
              </div>
            ))}
          </div>
          {aiNarrative && (
            <Card style={{ background: `${C.aiPurple}08`, border: `1px solid ${C.aiPurple}25`, padding: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: C.aiPurple, marginBottom: 8, textTransform: "uppercase", letterSpacing: .8 }}>✦ AI Scenario Analysis</div>
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{aiNarrative}</div>
            </Card>
          )}
          {!aiNarrative && (
            <div style={{ background: C.surface, border: `1px dashed ${C.border}`, borderRadius: 10, padding: 24, textAlign: "center", color: C.dim, fontSize: 12 }}>
              Adjust sliders and click AI Analysis to generate scenario narrative
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── PEER BENCHMARKS ───────────────────────────────────────────────
export function PeerBenchmarks() {
  const [group, setGroup] = useState("CA Banks $3–6B");
  const groups = ["CA Banks $3–6B", "Western US Community Banks", "All US Community Banks $2–8B"];

  function quartile(row: typeof PEER_DATA[0]) {
    const { vfb, p25, p50, p75, higher } = row;
    if (higher) {
      if (vfb >= p75) return { label: "Top Quartile",    color: C.forest   };
      if (vfb >= p50) return { label: "2nd Quartile",    color: C.gold     };
      if (vfb >= p25) return { label: "3rd Quartile",    color: C.rust     };
                      return { label: "Bottom Quartile", color: C.burgundy };
    } else {
      if (vfb <= p25) return { label: "Top Quartile",    color: C.forest   };
      if (vfb <= p50) return { label: "2nd Quartile",    color: C.gold     };
      if (vfb <= p75) return { label: "3rd Quartile",    color: C.rust     };
                      return { label: "Bottom Quartile", color: C.burgundy };
    }
  }

  const summary = [
    { label: "Top Quartile",  count: PEER_DATA.filter(r => quartile(r).color === C.forest  ).length, color: C.forest   },
    { label: "2nd Quartile",  count: PEER_DATA.filter(r => quartile(r).color === C.gold    ).length, color: C.gold     },
    { label: "Below Median",  count: PEER_DATA.filter(r => [C.rust, C.burgundy].includes(quartile(r).color)).length, color: C.burgundy },
  ];

  return (
    <div>
      <PH title="Peer Benchmarking" sub={`Demo Regional Bank vs. ${group}`} accentColor={C.navy} />
      <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
        {groups.map(g => (
          <button key={g} onClick={() => setGroup(g)} style={{ fontSize: 11, padding: "6px 14px", borderRadius: 20, border: `1px solid ${g === group ? C.navy : C.border}`, background: g === group ? C.navyBg : "transparent", color: g === group ? C.navy : C.muted, cursor: "pointer", fontWeight: g === group ? 700 : 400 }}>
            {g}
          </button>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 18 }}>
        {summary.map(({ label, count, color }, i) => (
          <div key={i} style={{ background: C.card, border: `1px solid ${color}25`, borderRadius: 10, padding: "12px 14px", textAlign: "center" }}>
            <div style={{ fontSize: 26, fontWeight: 800, color }}>{count}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{label} metrics</div>
          </div>
        ))}
      </div>
      <Card>
        <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 16 }}>Metric-by-Metric Comparison</div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: C.surface }}>
                {["Metric", "Your Bank", "25th %ile", "Median", "75th %ile", "Quartile"].map(h => (
                  <th key={h} style={{ padding: "9px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .5, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PEER_DATA.map((row, i) => {
                const q = quartile(row);
                return (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: "10px 12px", fontWeight: 600, color: C.text }}>{row.metric}</td>
                    <td style={{ padding: "10px 12px", fontWeight: 800, color: q.color }}>{row.format(row.vfb)}</td>
                    <td style={{ padding: "10px 12px", color: C.muted }}>{row.format(row.p25)}</td>
                    <td style={{ padding: "10px 12px", color: C.muted }}>{row.format(row.p50)}</td>
                    <td style={{ padding: "10px 12px", color: C.muted }}>{row.format(row.p75)}</td>
                    <td style={{ padding: "10px 12px" }}><Bdg label={q.label} color={q.color} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <SourcePill src="FDIC Statistics on Depository Institutions · S&P Global · Q4 2025" />
      </Card>
    </div>
  );
}

// ── EXAM READINESS ────────────────────────────────────────────────
export function ExamReadiness() {
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const composite = Math.round(EXAM_CATEGORIES.reduce((s, c) => s + c.score * c.weight, 0));
  const examColor = composite >= 90 ? C.forest : composite >= 75 ? C.gold : C.burgundy;
  const issueCount = EXAM_CATEGORIES.flatMap(c => c.items).filter(i => !i.ok).length;

  return (
    <div>
      <PH title="Exam Readiness Score" sub="Daily composite · FDIC Safety & Soundness · March 2, 2026" accentColor={examColor} />
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 20, marginBottom: 20, alignItems: "center" }}>
        <div style={{ textAlign: "center", background: C.card, border: `2px solid ${examColor}40`, borderRadius: 16, padding: "20px 28px" }}>
          <div style={{ fontSize: 52, fontWeight: 900, color: examColor, lineHeight: 1 }}>{composite}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>/ 100</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: examColor, marginTop: 6 }}>
            {composite >= 90 ? "Exam Ready" : composite >= 80 ? "Mostly Ready" : composite >= 70 ? "Needs Attention" : "High Risk"}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 12 }}>
            Composite score across {EXAM_CATEGORIES.length} examination categories. <span style={{ color: C.burgundy, fontWeight: 700 }}>{issueCount} open issues</span> require attention.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {EXAM_CATEGORIES.map((c, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: C.surface, borderRadius: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: c.color, width: 32 }}>{c.score}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: C.text, fontWeight: 600 }}>{c.cat}</div>
                  <Bar val={c.score} color={c.color} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {EXAM_CATEGORIES.map((cat, ci) => (
          <Card key={ci} style={{ padding: 0, overflow: "hidden" }}>
            <button
              onClick={() => setExpanded(p => ({ ...p, [ci]: !p[ci] }))}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "13px 16px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}
            >
              <div style={{ width: 40, height: 40, borderRadius: 10, background: `${cat.color}15`, border: `1px solid ${cat.color}30`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 16, color: cat.color, flexShrink: 0 }}>
                {cat.score}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{cat.cat}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Weight: {Math.round(cat.weight * 100)}% · {cat.items.filter(i => !i.ok).length} issue(s)</div>
              </div>
              <Bdg label={cat.score >= 90 ? "Strong" : cat.score >= 75 ? "Adequate" : "Needs Work"} color={cat.color} />
              <svg style={{ marginLeft: 4, transition: "transform .2s", transform: expanded[ci] ? "rotate(180deg)" : "rotate(0deg)" }} width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2 4l4 4 4-4" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {expanded[ci] && (
              <div style={{ padding: "0 16px 14px", borderTop: `1px solid ${C.border}` }}>
                {cat.items.map((item, ii) => (
                  <div key={ii} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: ii < cat.items.length - 1 ? `1px solid ${C.border}` : "none" }}>
                    <span style={{ fontSize: 14 }}>{item.ok ? "✅" : "⚠️"}</span>
                    <div style={{ flex: 1, fontSize: 12, color: C.text }}>{item.label}</div>
                    <div style={{ width: 80 }}><Bar val={item.score} color={item.ok ? C.forest : C.burgundy} /></div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: item.ok ? C.forest : C.burgundy, width: 30, textAlign: "right" }}>{item.score}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── AUDIT LOG ─────────────────────────────────────────────────────
export function AuditLogView() {
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("All");
  const [userFilter, setUserFilter] = useState("All");
  const [copied, setCopied] = useState(false);

  const catColor: Record<string, string> = {
    BSA: C.burgundy, Credit: C.forest, Finance: C.gold, Tasks: C.muted,
    Document: C.purple, Escalation: C.rust, "AI Chat": C.aiPurple, General: C.dim,
  };

  const CATS = ["All", "BSA", "Credit", "Finance", "Document", "Escalation", "AI Chat", "Tasks", "General"];
  const allUsers = ["All", ...Array.from(new Set(_auditLog.map(e => e.user).filter(Boolean)))];

  const filtered = _auditLog.filter(e => {
    const matchCat = catFilter === "All" || e.category === catFilter;
    const matchUser = userFilter === "All" || e.user === userFilter;
    const q = search.toLowerCase();
    const matchSearch = !q || e.action?.toLowerCase().includes(q) || e.detail?.toLowerCase().includes(q) || e.category?.toLowerCase().includes(q) || e.user?.toLowerCase().includes(q);
    return matchCat && matchUser && matchSearch;
  });

  function exportCSV() {
    const header = "Time,Category,Action,Detail,User";
    const rows = filtered.map(e => [e.time, e.category, e.action, e.detail, e.user].map(v => `"${(v||"").replace(/"/g,'""')}"`).join(","));
    const blob = new Blob([[header, ...rows].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "audit_trail.csv"; a.click();
    URL.revokeObjectURL(url);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div>
      <PH title="Audit Trail" sub={`${_auditLog.length} actions logged · Session: ${BANK.asOfDate}`} accentColor={C.purple} />

      {/* ── FILTERS ── */}
      <Card style={{ marginBottom: 14, padding: 14 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          {/* Search */}
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search actions, details…"
            style={{ flex: "1 1 200px", padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12, color: C.text, background: C.surface, outline: "none", fontFamily: "inherit" }}
          />
          {/* User filter */}
          <select
            value={userFilter}
            onChange={e => setUserFilter(e.target.value)}
            style={{ padding: "7px 10px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12, color: C.text, background: C.surface, outline: "none", fontFamily: "inherit", cursor: "pointer" }}
          >
            {allUsers.map(u => <option key={u} value={u}>{u === "All" ? "All Users" : u}</option>)}
          </select>
          {/* Export */}
          <button
            onClick={exportCSV}
            style={{ padding: "7px 14px", borderRadius: 8, border: `1px solid ${C.purple}`, background: copied ? C.purple : "transparent", color: copied ? "#fff" : C.purple, fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all .18s" }}
          >
            {copied ? "✓ Exported" : "⬇ Export CSV"}
          </button>
        </div>
        {/* Category pills */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
          {CATS.map(c => (
            <button key={c} onClick={() => setCatFilter(c)} style={{ fontSize: 10, padding: "4px 10px", borderRadius: 20, border: `1px solid ${catFilter === c ? (catColor[c] || C.purple) : C.border}`, background: catFilter === c ? `${catColor[c] || C.purple}15` : "transparent", color: catFilter === c ? (catColor[c] || C.purple) : C.muted, cursor: "pointer", fontWeight: catFilter === c ? 700 : 400 }}>
              {c}
            </button>
          ))}
        </div>
      </Card>

      {/* ── RESULTS COUNT ── */}
      <div style={{ fontSize: 11, color: C.dim, marginBottom: 8, paddingLeft: 2 }}>
        Showing {filtered.length} of {_auditLog.length} entries
        {(catFilter !== "All" || userFilter !== "All" || search) && (
          <button onClick={() => { setCatFilter("All"); setUserFilter("All"); setSearch(""); }} style={{ marginLeft: 8, background: "none", border: "none", color: C.burgundy, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
            Clear filters ×
          </button>
        )}
      </div>

      {_auditLog.length === 0 ? (
        <Card><div style={{ textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>No actions logged yet.</div></Card>
      ) : filtered.length === 0 ? (
        <Card><div style={{ textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>No entries match your filters.</div></Card>
      ) : (
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "90px 90px 1fr 110px 120px", background: C.surface, padding: "9px 16px", gap: 12 }}>
            {["Time", "Category", "Action", "Detail", "User"].map(h => (
              <div key={h} style={{ fontSize: 10, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .5 }}>{h}</div>
            ))}
          </div>
          {filtered.map((entry, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "90px 90px 1fr 110px 120px", padding: "10px 16px", gap: 12, borderTop: `1px solid ${C.border}`, background: i % 2 === 0 ? "transparent" : `${C.surface}80` }}>
              <div style={{ fontSize: 11, color: C.dim }}>{entry.time}</div>
              <div><Bdg label={entry.category} color={catColor[entry.category] || C.muted} /></div>
              <div style={{ fontSize: 12, color: C.text, fontWeight: 600 }}>{entry.action}</div>
              <div style={{ fontSize: 11, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={entry.detail}>{entry.detail}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{entry.user}</div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

// ── BOARD PACKAGE GENERATOR ───────────────────────────────────────
const BANK_CTX = "Assets: $4.21B, Net Income: $7.9M (+6.2% vs budget), NIM: 3.42% (-8bps QoQ), ROAA: 1.14%, ROAE: 11.8%, Efficiency: 58.3%, Tier 1: 12.8%, NPL: 0.61%, CRE Concentration: 287%, Deposits: $3.62B, Uninsured: 34.1%, ACL/Loans: 1.42%";

const BOARD_PACKAGES = [
  { title: "Monthly Executive Board Package", icon: "📊", prompt: "Generate a concise monthly board package for Demo Regional Bank for March 2026. Include: Executive Summary, Financial Highlights, Key Risks, Strategic Opportunities, and Regulatory Status. Be specific with numbers." },
  { title: "ALCO Meeting Package",            icon: "📉", prompt: "Generate an ALCO meeting package for Demo Regional Bank for March 14, 2026. Include: NIM trend analysis, rate sensitivity summary, funding mix, FHLB strategy recommendation." },
  { title: "Credit Committee Summary",         icon: "📋", prompt: "Generate a credit committee summary for Demo Regional Bank for March 2026. Include: criticized assets ($47.3M), CECL reserve, Ag drought exposure, CRE concentration, top action items." },
  { title: "Regulatory Exam Prep Summary",     icon: "🛡️", prompt: "Generate an exam prep summary for Demo Regional Bank's May 2026 FDIC exam. Include: BSA/AML status, CRA rating defense, capital adequacy, credit quality highlights, priority remediation items." },
];

export function BoardPackage({
  persona, onLog,
}: {
  persona: { name: string };
  onLog: (action: string, detail: string, category: string) => void;
}) {
  const [doc, setDoc] = useState<{ title: string; prompt: string } | null>(null);
  return (
    <div>
      <PH title="Board Package Generator" sub="One-click AI-generated executive documents" accentColor={C.navy} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        {BOARD_PACKAGES.map((pkg, i) => (
          <Card key={i} onClick={() => { setDoc(pkg); onLog("Generated document", pkg.title, "Document"); }} style={{ cursor: "pointer", padding: 20 }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>{pkg.icon}</div>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 6 }}>{pkg.title}</div>
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, marginBottom: 12 }}>AI-generated from live bank data · Includes all key metrics and recommended narrative.</div>
            <div style={{ fontSize: 12, color: C.burgundy, fontWeight: 700 }}>Generate document →</div>
          </Card>
        ))}
      </div>
      {doc && <DocModal title={doc.title} personaName={persona.name} prompt={doc.prompt} bankCtx={BANK_CTX} onClose={() => setDoc(null)} />}
    </div>
  );
}

// ── EXCEPTION QUEUE ───────────────────────────────────────────────
export function ExceptionQueue({ onLog }: { onLog: (a: string, d: string, c: string) => void }) {
  const [filter, setFilter] = useState<string>("all");
  const [items, setItems] = useState<(Exception & { resolved: boolean })[]>(
    EXCEPTIONS.map(e => ({ ...e, resolved: false }))
  );

  const sevColor: Record<string, string> = { critical: C.burgundy, high: C.gold, medium: C.rust, low: C.forest };
  const typeIcon: Record<string, string> = { "Wire": "⚡", "ACH": "🔄", "Positive Pay": "✉️", "Reg E": "📋", "Override": "🔓" };

  function resolve(id: string) {
    setItems(prev => prev.map(e => e.id === id ? { ...e, resolved: true } : e));
    const ex = items.find(e => e.id === id);
    if (ex) onLog("Resolved exception", `${ex.id}: ${ex.customer}`, "Operations");
  }

  const types = ["all", ...Array.from(new Set(EXCEPTIONS.map(e => e.type)))];
  const filtered = items.filter(e => (filter === "all" || e.type === filter) && !e.resolved);
  const resolved = items.filter(e => e.resolved).length;

  const critical  = filtered.filter(e => e.severity === "critical").length;
  const ppDeadline = filtered.filter(e => e.type === "Positive Pay" && e.deadline?.includes("Today")).length;

  return (
    <div>
      <PH title="Exception Queue" sub={`${filtered.length} open · ${resolved} resolved · As of ${BANK.asOfDate}`} accentColor={C.burgundy} />

      {/* Summary strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 18 }}>
        {[
          { label: "Open Exceptions", val: filtered.length, color: filtered.length > 8 ? C.burgundy : C.gold },
          { label: "Critical / Immediate", val: critical, color: critical > 0 ? C.burgundy : C.forest },
          { label: "Positive Pay (3pm)", val: ppDeadline, color: ppDeadline > 0 ? C.burgundy : C.forest },
          { label: "Resolved Today", val: resolved, color: C.forest },
        ].map(({ label, val, color }, i) => (
          <div key={i} style={{ background: C.card, border: `1px solid ${color}25`, borderRadius: 10, padding: "12px 16px", textAlign: "center" }}>
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: .6, marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 26, fontWeight: 900, color }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Positive Pay deadline banner */}
      {ppDeadline > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 16px", background: `${C.burgundy}08`, border: `1.5px solid ${C.burgundy}40`, borderRadius: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 16 }}>⏰</span>
          <div style={{ flex: 1, fontSize: 12, color: C.burgundy, fontWeight: 700 }}>
            {ppDeadline} Positive Pay decision{ppDeadline > 1 ? "s" : ""} expire at 3:00 PM TODAY — auto-return if unresolved
          </div>
          <Bar val={100} color={C.burgundy} />
        </div>
      )}

      {/* Type filters */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        {types.map(t => (
          <button key={t} onClick={() => setFilter(t)} style={{ fontSize: 11, padding: "5px 13px", borderRadius: 20, border: `1px solid ${filter === t ? C.burgundy : C.border}`, background: filter === t ? `${C.burgundy}10` : "transparent", color: filter === t ? C.burgundy : C.muted, cursor: "pointer", fontWeight: filter === t ? 700 : 400, textTransform: "capitalize" }}>
            {typeIcon[t] || ""} {t}
          </button>
        ))}
      </div>

      {/* Exception list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {filtered
          .sort((a, b) => ({ critical: 0, high: 1, medium: 2, low: 3 } as any)[a.severity] - ({ critical: 0, high: 1, medium: 2, low: 3 } as any)[b.severity])
          .map(ex => (
            <Card key={ex.id} style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ display: "flex" }}>
                <div style={{ width: 4, background: sevColor[ex.severity], flexShrink: 0, borderRadius: "12px 0 0 12px" }} />
                <div style={{ padding: "13px 16px", flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <div style={{ fontSize: 18, flexShrink: 0 }}>{typeIcon[ex.type] || "📌"}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 4 }}>
                        <div>
                          <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{ex.customer}</span>
                          <span style={{ marginLeft: 8, fontSize: 10, color: C.dim, fontWeight: 600 }}>{ex.id}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                          <Bdg label={ex.type} color={sevColor[ex.severity]} />
                          <Bdg label={ex.severity} color={sevColor[ex.severity]} />
                        </div>
                      </div>
                      <div style={{ fontSize: 12, color: C.muted, marginBottom: 6, lineHeight: 1.5 }}>{ex.issue}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: C.text }}>${ex.amount.toLocaleString()}</span>
                        <span style={{ fontSize: 10, color: C.dim }}>{ex.category}</span>
                        <span style={{ fontSize: 10, color: ex.age === 0 ? C.forest : ex.age > 5 ? C.burgundy : C.gold }}>
                          {ex.age === 0 ? "Received today" : `${ex.age}d old`}
                        </span>
                        {ex.deadline && (
                          <span style={{ fontSize: 10, fontWeight: 700, color: C.burgundy, background: `${C.burgundy}10`, padding: "2px 8px", borderRadius: 10 }}>
                            ⏱ {ex.deadline}
                          </span>
                        )}
                        {ex.assignee && (
                          <span style={{ fontSize: 10, color: C.muted }}>→ {ex.assignee}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => resolve(ex.id)}
                      style={{ flexShrink: 0, background: C.forestBg, border: `1px solid ${C.forest}30`, borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 11, color: C.forest, fontWeight: 700 }}
                    >
                      ✓ Resolve
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
      </div>

      {filtered.length === 0 && (
        <Card><div style={{ textAlign: "center", padding: 40, color: C.forest, fontSize: 13, fontWeight: 700 }}>✓ All exceptions resolved for this filter</div></Card>
      )}

      <SourcePill src="FIS Horizon Exception Manager · Nacha ACH · FedWire · OFAC SDN" />
    </div>
  );
}

// ── LENDING PIPELINE VIEW ──────────────────────────────────────────
export function LendingPipelineView({ onLog }: { onLog: (a: string, d: string, c: string) => void }) {
  const [sort, setSort] = useState<"amount" | "daysIn" | "stage">("daysIn");
  const [filter, setFilter] = useState<string>("all");

  const stageColor: Record<string, string> = {
    "Approved": C.forest, "Committee": C.gold, "Underwriting": C.rust,
    "Processing": C.gold, "Pre-approval": C.cyan, "Term Sheet": C.cyan, "SBA Review": C.muted,
  };

  const types = ["all", ...Array.from(new Set(LENDING_PIPELINE.map(l => l.type)))];
  const filtered = (filter === "all" ? LENDING_PIPELINE : LENDING_PIPELINE.filter(l => l.type === filter))
    .sort((a, b) =>
      sort === "amount" ? b.amount - a.amount :
      sort === "daysIn" ? b.daysIn - a.daysIn : 0
    );

  const total = filtered.reduce((s, l) => s + l.amount, 0);
  const stalled = LENDING_PIPELINE.filter(l => l.issue).length;

  return (
    <div>
      <PH title="Loan Pipeline" sub={`${LENDING_PIPELINE.length} opportunities · ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(LENDING_PIPELINE.reduce((s, l) => s + l.amount, 0))} total · Demo Regional Bank`} accentColor={C.cyan} />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 18 }}>
        {[
          { label: "Total Pipeline",    val: `$${(total / 1e6).toFixed(1)}M`,   color: C.cyan     },
          { label: "Stalled / Issues",  val: stalled,                            color: C.burgundy },
          { label: "SBA in Process",    val: LENDING_PIPELINE.filter(l => l.type.startsWith("SBA")).length, color: C.gold },
          { label: "Avg Days in Stage", val: Math.round(LENDING_PIPELINE.reduce((s, l) => s + l.daysIn, 0) / LENDING_PIPELINE.length) + "d", color: C.rust },
        ].map(({ label, val, color }, i) => (
          <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 16px", textAlign: "center" }}>
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: .6, marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 900, color }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Filters & sort */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 6 }}>
          {types.map(t => (
            <button key={t} onClick={() => setFilter(t)} style={{ fontSize: 11, padding: "5px 12px", borderRadius: 20, border: `1px solid ${filter === t ? C.cyan : C.border}`, background: filter === t ? `${C.cyan}12` : "transparent", color: filter === t ? C.cyan : C.muted, cursor: "pointer", fontWeight: filter === t ? 700 : 400 }}>
              {t}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: C.muted }}>Sort:</span>
          {([["amount", "$ Amount"], ["daysIn", "Days in Stage"]] as const).map(([k, l]) => (
            <button key={k} onClick={() => setSort(k)} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20, border: `1px solid ${sort === k ? C.cyan : C.border}`, background: sort === k ? `${C.cyan}12` : "transparent", color: sort === k ? C.cyan : C.muted, cursor: "pointer", fontWeight: sort === k ? 700 : 400 }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2.5fr 1fr 1fr 1fr 1fr 100px", padding: "10px 16px", background: C.surface, gap: 12 }}>
          {["Borrower / ID", "Amount", "Type", "Stage", "Days", "Close Target"].map(h => (
            <div key={h} style={{ fontSize: 10, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .5 }}>{h}</div>
          ))}
        </div>
        {filtered.map((lp, i) => (
          <div
            key={lp.id}
            onClick={() => onLog("Viewed pipeline item", lp.borrower, "Lending")}
            style={{ display: "grid", gridTemplateColumns: "2.5fr 1fr 1fr 1fr 1fr 100px", padding: "13px 16px", gap: 12, borderTop: `1px solid ${C.border}`, alignItems: "center", cursor: "pointer", background: lp.issue ? `${C.burgundy}04` : "transparent" }}
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = C.surface}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = lp.issue ? `${C.burgundy}04` : "transparent"}
          >
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{lp.borrower}</div>
              <div style={{ fontSize: 10, color: C.dim, marginTop: 1 }}>{lp.id} · {lp.rm}</div>
              {lp.issue && <div style={{ fontSize: 10, color: C.burgundy, fontWeight: 600, marginTop: 3 }}>⚠ {lp.issue}</div>}
            </div>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>${(lp.amount / 1e6).toFixed(1)}M</div>
            <div><Bdg label={lp.type} color={C.cyan} /></div>
            <div style={{ fontSize: 11, fontWeight: 700, color: stageColor[lp.stage] || C.muted }}>{lp.stage}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: lp.daysIn > 20 ? C.burgundy : lp.daysIn > 10 ? C.gold : C.forest }}>{lp.daysIn}d</div>
            <div style={{ fontSize: 11, color: C.muted }}>{lp.closeTarget}</div>
          </div>
        ))}
      </Card>

      <SourcePill src="FIS Horizon LOS · SBA ETRAN · AFS Credit Platform" />
    </div>
  );
}

// ── MORNING BRIEFING ──────────────────────────────────────────────
const BRIEFING_CTX = "Demo Regional Bank: $4.21B assets, NIM 3.42% (-8bps QoQ), Net Income $7.9M (+6.2% vs budget), NPL 0.61%, CRE Concentration 287%, Deposits $3.62B (34.1% uninsured), CECL 1.42%, Tier 1 12.8%. Date: March 2, 2026.";

export function MorningBriefing({
  pid, persona,
}: {
  pid: string;
  persona: { grad: string; name?: string; icon?: string };
}) {
  const [dismissed, setDismissed] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [loading, setLoading] = useState(false);
  const [aiItems, setAiItems] = useState<{ icon: string; text: string; urgency: string }[] | null>(null);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [aiError, setAiError] = useState(false);

  const b = BRIEFINGS[pid];
  const urgColor: Record<string, string> = { critical: C.burgundy, high: C.gold, ok: C.forest, medium: C.rust };
  const items = aiItems ?? b?.items ?? [];
  const summary = aiSummary ?? b?.summary ?? "";

  async function refresh() {
    setLoading(true);
    setAiError(false);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 500,
          system: `You generate morning briefings for bank executives. Return ONLY valid JSON with keys: summary (string, 1 sentence) and items (array of exactly 4 objects, each with icon (emoji string), text (string, ≤120 chars, specific with numbers), urgency (one of: critical|high|ok)). No markdown, no extra text.`,
          messages: [{ role: "user", content: `Generate morning briefing for ${pid} role at Demo Regional Bank. Context: ${BRIEFING_CTX} Persona hint: ${b?.greeting ?? ""}` }],
        }),
      });
      const d = await res.json();
      const raw = d.content?.find((x: any) => x.type === "text")?.text ?? "";
      const parsed = JSON.parse(raw);
      if (parsed.items?.length && parsed.summary) {
        setAiItems(parsed.items);
        setAiSummary(parsed.summary);
      } else { setAiError(true); }
    } catch { setAiError(true); }
    setLoading(false);
  }

  if (dismissed || !b) return null;
  return (
    <div style={{ background: `linear-gradient(135deg,${C.burgundy}06,${C.gold}04)`, border: `1px solid ${C.gold}35`, borderRadius: 12, marginBottom: 16 }}>
      {/* Compact header — always visible */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", cursor: "pointer" }} onClick={() => setCollapsed(c => !c)}>
        <div style={{ width: 32, height: 32, borderRadius: 9, background: persona.grad, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>☀️</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{b.greeting}</span>
            <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 8, background: aiItems ? `${C.aiPurple}12` : C.goldBg, color: aiItems ? C.aiPurple : C.gold, fontWeight: 700, border: `1px solid ${aiItems ? C.aiPurple + "30" : C.gold + "30"}` }}>
              {aiItems ? "✦ AI Live" : `Briefing · ${BANK.asOfDate}`}
            </span>
            {aiError && <span style={{ fontSize: 10, color: C.burgundy }}>AI unavailable</span>}
          </div>
          {collapsed && <div style={{ fontSize: 11, color: C.muted, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{summary}</div>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 10, color: C.muted }}>{collapsed ? "▼ Expand" : "▲ Collapse"}</span>
          <button onClick={e => { e.stopPropagation(); setDismissed(true); }} style={{ background: "none", border: "none", color: C.dim, cursor: "pointer", fontSize: 14, padding: "0 4px", lineHeight: 1 }}>✕</button>
        </div>
      </div>
      {/* Expanded content */}
      {!collapsed && (
        <div style={{ padding: "0 14px 14px" }}>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>{summary}</div>
          <div className="briefing-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {loading
              ? [0,1,2,3].map(i => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 11px", background: C.card, borderRadius: 9, border: `1px solid ${C.border}` }}>
                    <div style={{ display: "flex", gap: 3 }}>
                      {[0,1,2].map(j => <div key={j} style={{ width: 5, height: 5, borderRadius: "50%", background: C.aiPurple, animation: `dot .9s ${j * .2}s infinite` }} />)}
                    </div>
                  </div>
                ))
              : items.map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "9px 11px", background: C.card, borderRadius: 9, border: `1px solid ${(urgColor[item.urgency] || C.muted) + "25"}` }}>
                    <span style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }}>{item.icon}</span>
                    <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{item.text}</div>
                  </div>
                ))}
          </div>
          <button
            onClick={refresh}
            disabled={loading}
            style={{ marginTop: 10, background: loading ? C.surface : `${C.aiPurple}10`, border: `1px solid ${C.aiPurple}30`, borderRadius: 8, padding: "5px 12px", cursor: loading ? "default" : "pointer", fontSize: 11, color: C.aiPurple, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}
          >
            {loading
              ? <>{[0,1,2].map(i => <div key={i} style={{ width: 4, height: 4, borderRadius: "50%", background: C.aiPurple, animation: `dot .9s ${i * .2}s infinite` }} />)} Generating…</>
              : <>✦ {aiItems ? "Regenerate with AI" : "Generate live AI briefing"}</>
            }
          </button>
        </div>
      )}
    </div>
  );
}

// ── SUBBAR ────────────────────────────────────────────────────────
export function SubBar({
  pid, persona,
}: {
  pid: string;
  persona: { initials: string; name: string; full: string };
}) {
  const [open, setOpen] = useState(false);
  const cfg = SUBBAR_CONFIG[pid];
  return (
    <div style={{ background: C.burgundyDark, borderTop: "1px solid rgba(255,255,255,.08)" }}>
      <div style={{ padding: "0 24px", display: "flex", alignItems: "center", height: 36, gap: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <div style={{ width: 22, height: 22, borderRadius: 6, background: "rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, color: "#fff", flexShrink: 0 }}>
            {persona.initials}
          </div>
          <div>
            <span style={{ fontSize: 12, color: "#fff", fontWeight: 700 }}>{persona.name}</span>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,.45)", marginLeft: 6 }}>{persona.full}</span>
          </div>
        </div>
        <span style={{ color: "rgba(255,255,255,.18)" }}>·</span>
        <span style={{ fontSize: 11, color: C.goldLight, fontWeight: 600 }}>{cfg.label}</span>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          {cfg.status.map(({ l, v }, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 10, color: "rgba(255,255,255,.35)" }}>{l}:</span>
              <span style={{ fontSize: 10, color: "#4ade80", fontWeight: 700 }}>{v}</span>
            </div>
          ))}
          <span style={{ color: "rgba(255,255,255,.18)", fontSize: 14 }}>|</span>
          <button onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", gap: 5, background: open ? "rgba(255,255,255,.12)" : "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.15)", borderRadius: 14, padding: "3px 10px", cursor: "pointer", color: "rgba(255,255,255,.6)", fontSize: 10, fontWeight: 600 }}>
            <span>System Info</span>
            <svg style={{ transition: "transform .2s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }} width="9" height="9" viewBox="0 0 10 10" fill="none">
              <path d="M2 3.5l3 3 3-3" stroke="rgba(255,255,255,.6)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
      {open && (
        <div style={{ padding: "10px 24px 12px", borderTop: "1px solid rgba(255,255,255,.08)", display: "flex", gap: 32 }}>
          {cfg.details.map(({ icon, label, value }, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 7 }}>
              <span style={{ fontSize: 13, marginTop: 1 }}>{icon}</span>
              <div>
                <div style={{ fontSize: 9, fontWeight: 800, color: "rgba(255,255,255,.3)", textTransform: "uppercase", letterSpacing: .6, marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,.55)" }}>{value}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── FRAUD / CARD DISPUTE QUEUE ────────────────────────────────────
export function FraudDisputeQueue({ onLog }: { onLog: (a: string, d: string, c: string) => void }) {
  const [items, setItems] = useState(FRAUD_DISPUTES.map(f => ({ ...f, resolved: f.status === "Resolved" || f.status === "Blocked" })));
  const [filter, setFilter] = useState("all");
  const prioColor: Record<string,string> = { critical: C.burgundy, high: C.gold, medium: C.rust, low: C.forest };
  const typeIcon: Record<string,string> = { "Card Fraud": "💳", "Dispute": "📋", "Synthetic ID": "👤" };

  function resolve(id: string) {
    setItems(prev => prev.map(f => f.id === id ? { ...f, resolved: true, status: "Resolved" } : f));
    onLog("Resolved fraud/dispute", id, "Fraud");
  }

  const cats = ["all", "Card Fraud", "Dispute", "Synthetic ID"];
  const filtered = filter === "all" ? items : items.filter(f => f.type === filter);

  return (
    <div>
      <PH title="Fraud & Card Dispute Queue" sub={`${items.filter(f => !f.resolved).length} open · ${items.filter(f => f.risk === "critical").length} critical`} accentColor={C.burgundy} />
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        {cats.map(c => (
          <button key={c} onClick={() => setFilter(c)} style={{ fontSize: 10, padding: "4px 10px", borderRadius: 20, border: `1px solid ${filter===c ? C.burgundy : C.border}`, background: filter===c ? C.burgundyBg : "transparent", color: filter===c ? C.burgundy : C.muted, cursor: "pointer", fontWeight: filter===c ? 700 : 400, textTransform: "capitalize" }}>{c}</button>
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filtered.map((f, i) => (
          <Card key={i} style={{ padding: 0, overflow: "hidden", opacity: f.resolved ? 0.6 : 1 }}>
            <div style={{ display: "flex" }}>
              <div style={{ width: 3, background: prioColor[f.risk] || C.muted, flexShrink: 0 }} />
              <div style={{ padding: "13px 15px", flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 16 }}>{typeIcon[f.type] || "❓"}</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{f.customer}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{f.type} · {f.channel}</div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>${f.amount.toLocaleString()}</div>
                    <div style={{ fontSize: 10, color: C.dim }}>{f.date}</div>
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Bdg label={f.risk} color={prioColor[f.risk] || C.muted} />
                    <Bdg label={f.status} color={f.resolved ? C.muted : f.status === "Investigating" ? C.gold : C.burgundy} />
                    {f.provisionalCredit && <Bdg label="Prov. Credit" color={C.forest} />}
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {f.deadline && <span style={{ fontSize: 10, color: C.burgundy, fontWeight: 700 }}>Due: {f.deadline}</span>}
                    {!f.resolved && (
                      <button onClick={() => resolve(f.id)} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 7, border: `1px solid ${C.forest}`, background: `${C.forest}12`, color: C.forest, cursor: "pointer", fontWeight: 700 }}>Resolve</button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── IT TICKET TRACKER ─────────────────────────────────────────────
export function ITTicketPanel({ onLog }: { onLog: (a: string, d: string, c: string) => void }) {
  const [filter, setFilter] = useState("all");
  const prioColor: Record<string,string> = { critical: C.burgundy, high: C.gold, medium: C.rust };
  const statColor: Record<string,string> = { "In Progress": C.gold, "Escalated": C.burgundy, "Pending": C.muted, "Breached": "#8B0000" };

  const cats = ["all", ...Array.from(new Set(IT_TICKETS.map(t => t.cat)))];
  const filtered = filter === "all" ? IT_TICKETS : IT_TICKETS.filter(t => t.cat === filter);
  const breached = IT_TICKETS.filter(t => t.status === "Breached").length;

  return (
    <div>
      <PH title="IT Ticket Dashboard" sub={`${IT_TICKETS.length} open · ${breached} SLA breached · ${IT_TICKETS.filter(t=>t.priority==="critical").length} critical`} accentColor={C.navy} />
      {breached > 0 && (
        <Card style={{ marginBottom: 14, background: `${C.burgundy}08`, border: `1px solid ${C.burgundy}30`, padding: "12px 14px" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.burgundy }}>⚠ {breached} ticket{breached > 1 ? "s" : ""} have breached SLA — examiner visibility risk if unresolved before May FDIC exam</div>
        </Card>
      )}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        {cats.map(c => (
          <button key={c} onClick={() => setFilter(c)} style={{ fontSize: 10, padding: "4px 10px", borderRadius: 20, border: `1px solid ${filter===c ? C.navy : C.border}`, background: filter===c ? `${C.navy}15` : "transparent", color: filter===c ? C.navy : C.muted, cursor: "pointer", fontWeight: filter===c ? 700 : 400, textTransform: "capitalize" }}>{c}</button>
        ))}
      </div>
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "80px 2fr 100px 80px 60px 90px", padding: "9px 16px", background: C.surface, gap: 10 }}>
          {["Ticket","Title","Category","Assignee","SLA","Status"].map(h => (
            <div key={h} style={{ fontSize: 10, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .4 }}>{h}</div>
          ))}
        </div>
        {filtered.map((t, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "80px 2fr 100px 80px 60px 90px", padding: "12px 16px", borderTop: `1px solid ${C.border}`, gap: 10, alignItems: "center", background: t.status === "Breached" ? `${C.burgundy}06` : "transparent" }}>
            <div>
              <div style={{ fontSize: 10, color: C.dim }}>{t.id}</div>
              <Bdg label={t.priority} color={prioColor[t.priority] || C.muted} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{t.title}</div>
              <div style={{ fontSize: 10, color: C.dim, marginTop: 1 }}>{t.hoursOpen}hr open · {t.created}</div>
            </div>
            <div style={{ fontSize: 11, color: C.muted }}>{t.cat}</div>
            <div style={{ fontSize: 11, color: C.muted }}>{t.assignee}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: t.status === "Breached" ? C.burgundy : C.muted }}>{t.sla}</div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.status === "Breached" ? "#8B0000" : statColor[t.status] || C.muted }}>{t.status}</div>
              {t.status === "Breached" && <div style={{ fontSize: 9, color: C.burgundy }}>SLA exceeded</div>}
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ── BCP / DISASTER RECOVERY DASHBOARD ────────────────────────────
export function BCPDashboard() {
  const bcp = BCP_DATA;
  const scoreColor = (s: number) => s >= 85 ? C.forest : s >= 70 ? C.gold : C.burgundy;
  const statusColor: Record<string,string> = { Tested: C.forest, Stale: C.gold, Overdue: C.burgundy };

  return (
    <div>
      <PH title="Business Continuity / Disaster Recovery" sub={`Last full test: ${bcp.lastFullTest} · Next scheduled: ${bcp.nextScheduled}`} accentColor={C.navy} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 18 }}>
        <Card style={{ padding: 16, textAlign: "center" }}>
          <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: .6, marginBottom: 6 }}>Overall BCP Score</div>
          <div style={{ fontSize: 36, fontWeight: 800, color: scoreColor(bcp.overallScore) }}>{bcp.overallScore}</div>
          <div style={{ fontSize: 11, color: C.muted }}>/ 100</div>
        </Card>
        <Card style={{ padding: 16, textAlign: "center" }}>
          <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: .6, marginBottom: 6 }}>Components Tested</div>
          <div style={{ fontSize: 36, fontWeight: 800, color: C.forest }}>{bcp.components.filter(c => c.status === "Tested").length}</div>
          <div style={{ fontSize: 11, color: C.muted }}>of {bcp.components.length} total</div>
        </Card>
        <Card style={{ padding: 16, textAlign: "center" }}>
          <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: .6, marginBottom: 6 }}>Overdue / Stale</div>
          <div style={{ fontSize: 36, fontWeight: 800, color: C.burgundy }}>{bcp.components.filter(c => c.status !== "Tested").length}</div>
          <div style={{ fontSize: 11, color: C.muted }}>need attention</div>
        </Card>
      </div>
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 60px 60px 80px 80px 70px", padding: "9px 16px", background: C.surface, gap: 10 }}>
          {["Component","Score","RTO","RPO","Last Test","Status"].map(h => (
            <div key={h} style={{ fontSize: 10, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .4 }}>{h}</div>
          ))}
        </div>
        {bcp.components.map((c, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 60px 60px 80px 80px 70px", padding: "12px 16px", borderTop: `1px solid ${C.border}`, gap: 10, alignItems: "center", background: c.status !== "Tested" ? `${C.gold}06` : "transparent" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{c.name}</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: scoreColor(c.score) }}>{c.score}</div>
            <div style={{ fontSize: 11, color: C.muted }}>{c.rto}</div>
            <div style={{ fontSize: 11, color: C.muted }}>{c.rpo}</div>
            <div style={{ fontSize: 10, color: C.dim }}>{c.lastTested}</div>
            <Bdg label={c.status} color={statusColor[c.status] || C.muted} />
          </div>
        ))}
      </Card>
    </div>
  );
}

// ── WATCHLIST PANEL (Credit tab view) ─────────────────────────────
export function WatchlistPanel({ onLog }: { onLog: (a: string, d: string, c: string) => void }) {
  const [signoffs, setSignoffs] = useState<Record<string,boolean>>({});
  const clsColor: Record<string,string> = { "Special Mention": C.gold, Substandard: C.burgundy, Doubtful: "#8B0000" };

  function signOff(id: string) {
    setSignoffs(s => ({ ...s, [id]: true }));
    onLog("Signed off watchlist credit", id, "Credit");
  }

  return (
    <div>
      <PH title="Watchlist Committee" sub={`${WATCHLIST_CREDITS.length} credits · ${Object.keys(signoffs).length} signed off this session`} accentColor={C.burgundy} />
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {WATCHLIST_CREDITS.map(credit => (
          <Card key={credit.id} style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ display: "flex" }}>
              <div style={{ width: 3, background: clsColor[credit.cls] || C.muted, flexShrink: 0 }} />
              <div style={{ padding: "14px 16px", flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 14, color: C.text }}>{credit.name}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{credit.id} · {credit.type} · RM: {credit.rm}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>${(credit.bal/1e6).toFixed(2)}M</div>
                    <Bdg label={credit.cls} color={clsColor[credit.cls] || C.muted} />
                  </div>
                </div>
                <p style={{ margin: "0 0 10px", fontSize: 12, color: C.muted, lineHeight: 1.6 }}>{credit.creditMemo.substring(0, 200)}…</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 10 }}>
                  {[["DSCR", credit.dscr?.toFixed(2) ?? "N/A"], ["LTV", credit.ltv ? `${(credit.ltv*100).toFixed(0)}%` : "N/A"], ["Rate", `${credit.rate}%`], ["Maturity", credit.maturity || "N/A"]].map(([l,v], i) => (
                    <div key={i} style={{ background: C.surface, borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                      <div style={{ fontSize: 9, color: C.dim, textTransform: "uppercase", letterSpacing: .4 }}>{l}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginTop: 2 }}>{v}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginBottom: 10 }}>
                  {credit.actions.map((a, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, padding: "6px 0", borderBottom: i < credit.actions.length-1 ? `1px solid ${C.border}` : "none", alignItems: "center" }}>
                      <div style={{ width: 16, height: 16, borderRadius: "50%", background: a.done ? C.forest : C.surface, border: `2px solid ${a.done ? C.forest : C.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {a.done && <span style={{ color: "#fff", fontSize: 9 }}>✓</span>}
                      </div>
                      <span style={{ fontSize: 11, color: a.done ? C.dim : C.text, textDecoration: a.done ? "line-through" : "none" }}>{a.action}</span>
                      <span style={{ fontSize: 10, color: C.dim, marginLeft: "auto" }}>{a.date}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={() => signOff(credit.id)} disabled={signoffs[credit.id]} style={{ flex: 1, padding: "9px", borderRadius: 9, border: `1px solid ${signoffs[credit.id] ? C.border : C.forest}`, background: signoffs[credit.id] ? C.surface : `${C.forest}12`, color: signoffs[credit.id] ? C.muted : C.forest, cursor: signoffs[credit.id] ? "default" : "pointer", fontWeight: 700, fontSize: 12 }}>
                    {signoffs[credit.id] ? "✓ Watchlist Confirmed" : "✓ Sign Off Watchlist"}
                  </button>
                  <button onClick={() => onLog("Adverse classification memo initiated", credit.name, "Credit")} style={{ padding: "9px 16px", borderRadius: 9, border: `1px solid ${C.burgundy}`, background: `${C.burgundy}10`, color: C.burgundy, cursor: "pointer", fontWeight: 700, fontSize: 12 }}>Adverse Memo</button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── MORTGAGE PIPELINE PANEL ───────────────────────────────────────
export function MortgagePipelinePanel({ onLog }: { onLog: (a: string, d: string, c: string) => void }) {
  const [filter, setFilter] = useState("All");
  const MORT_COLOR = "#6B3D8A";
  const statusColor: Record<string,string> = { Approved: C.forest, Underwriting: C.gold, Processing: C.gold, Conditional: C.gold, "Docs Ordered": C.forest, Suspended: C.burgundy };
  const filtered = filter === "All" ? MORTGAGE_PIPELINE : MORTGAGE_PIPELINE.filter(m => m.purpose === filter || m.status === filter);

  return (
    <div>
      <PH title="Mortgage & Consumer Pipeline" sub={`${MORTGAGE_PIPELINE.length} loans · $${(MORTGAGE_PIPELINE.reduce((s,m)=>s+m.amount,0)/1e6).toFixed(1)}M · ${MORTGAGE_PIPELINE.filter(m=>m.daysToLock<=14).length} locks expiring ≤14 days`} accentColor={MORT_COLOR} />
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        {["All","Purchase","Refi","Approved","Suspended"].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ fontSize: 10, padding: "4px 10px", borderRadius: 20, border: `1px solid ${filter===f ? MORT_COLOR : C.border}`, background: filter===f ? `${MORT_COLOR}15` : "transparent", color: filter===f ? MORT_COLOR : C.muted, cursor: "pointer", fontWeight: filter===f ? 700 : 400 }}>{f}</button>
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filtered.map((m, i) => (
          <Card key={i} style={{ padding: 0, overflow: "hidden", border: m.status === "Suspended" ? `1px solid ${C.burgundy}40` : undefined }}>
            <div style={{ display: "flex" }}>
              <div style={{ width: 3, background: m.daysToLock <= 14 ? C.burgundy : statusColor[m.status] || C.muted, flexShrink: 0 }} />
              <div style={{ padding: "13px 15px", flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{m.borrower}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{m.id} · {m.purpose} · {m.type}{m.lmi ? " · CRA/LMI" : ""}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>${(m.amount/1e3).toFixed(0)}K</div>
                    <div style={{ fontSize: 10, color: m.rate ? C.muted : C.dim }}>{m.rate ? `${m.rate}%` : "Rate TBD"}</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <Bdg label={m.status} color={statusColor[m.status] || C.muted} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: m.daysToLock <= 14 ? C.burgundy : m.daysToLock <= 21 ? C.gold : C.muted }}>Lock: {m.daysToLock}d left</span>
                  <span style={{ fontSize: 10, color: C.dim }}>FICO {m.fico} · LTV {(m.ltv*100).toFixed(0)}%</span>
                  {m.fico < 660 && <Bdg label="Low FICO" color={C.burgundy} />}
                  {m.daysToLock <= 14 && <Bdg label="Lock Expiring" color={C.burgundy} />}
                </div>
                {m.lmi && <div style={{ marginTop: 6, fontSize: 10, color: C.forest, fontWeight: 700 }}>● CRA/LMI — counts toward lending test</div>}
                <div style={{ marginTop: 6, fontSize: 11, color: C.dim }}>Close target: {m.closeTarget} · {m.daysIn} days in pipeline</div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── WEALTH ACCOUNTS PANEL ─────────────────────────────────────────
export function WealthAccountsPanel({ onLog }: { onLog: (a: string, d: string, c: string) => void }) {
  const w = WEALTH_DATA;
  const WEALTH_COLOR = "#2C6B5F";
  const statusColor: Record<string,string> = { Current: C.forest, "At Risk": C.burgundy, Overdue: C.gold };
  const prioColor: Record<string,string> = { critical: C.burgundy, high: C.gold, medium: C.rust };

  return (
    <div>
      <PH title="Wealth & Trust — Account Management" sub={`${w.accounts} accounts · $${(w.aum/1e6).toFixed(0)}M AUM · $${(w.feeIncomeMTD/1e3).toFixed(0)}K fee income MTD`} accentColor={WEALTH_COLOR} />
      {w.fiduciaryAlerts.filter(a => a.priority === "critical" || a.priority === "high").map((a, i) => (
        <Card key={i} style={{ marginBottom: 10, padding: "12px 14px", background: `${prioColor[a.priority]}08`, border: `1px solid ${prioColor[a.priority]}30` }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: prioColor[a.priority] }}>⚠ {a.type}: {a.account}</div>
          {a.detail && <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{a.detail}</div>}
        </Card>
      ))}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {w.accounts_list.map((acc, i) => (
          <Card key={i} style={{ padding: 0, overflow: "hidden", border: acc.status !== "Current" ? `1px solid ${statusColor[acc.status]}40` : undefined }}>
            <div style={{ display: "flex" }}>
              <div style={{ width: 3, background: statusColor[acc.status] || C.muted, flexShrink: 0 }} />
              <div style={{ padding: "13px 15px", flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{acc.name}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{acc.type} · CM: {acc.cm} · {acc.riskProfile}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: WEALTH_COLOR }}>${(acc.aum/1e6).toFixed(1)}M</div>
                    <div style={{ fontSize: 10, color: C.dim }}>Fee YTD: ${(acc.feeYTD/1e3).toFixed(0)}K</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <Bdg label={acc.status} color={statusColor[acc.status] || C.muted} />
                  <span style={{ fontSize: 11, color: C.muted }}>Next review: {acc.nextReview}</span>
                  {acc.status !== "Current" && <Bdg label="Action Required" color={prioColor["high"]} />}
                </div>
                <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                  <button onClick={() => onLog("Viewed account", acc.name, "Wealth")} style={{ fontSize: 11, padding: "5px 12px", borderRadius: 7, border: `1px solid ${WEALTH_COLOR}`, background: `${WEALTH_COLOR}12`, color: WEALTH_COLOR, cursor: "pointer", fontWeight: 700 }}>View File</button>
                  {acc.status !== "Current" && <button onClick={() => onLog("Initiated review", acc.name, "Wealth")} style={{ fontSize: 11, padding: "5px 12px", borderRadius: 7, border: `1px solid ${C.gold}`, background: `${C.gold}12`, color: C.gold, cursor: "pointer", fontWeight: 700 }}>Schedule Review</button>}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── ALCO TOOLS PANEL ──────────────────────────────────────────────
export function ALCOToolsPanel({ onLog }: { onLog: (a: string, d: string, c: string) => void }) {
  const [rateShift, setRateShift] = useState(0);
  const [loanAdj, setLoanAdj] = useState(0);
  const CRO_COLOR = "#1E3A5F";
  const ip = INVESTMENT_PORTFOLIO;
  const cd = CD_MATURITY_LADDER;
  const lm = LIQUIDITY_METRICS;

  // NII sensitivity modeling
  const baseNII = 38400000;
  const rateSens = TEST_DATA?.alm?.rateSensitivity || [];
  const nearestScenario = rateSens.reduce((prev: any, curr: any) => Math.abs(curr.pct - rateShift/100) < Math.abs(prev.pct - rateShift/100) ? curr : prev, rateSens[3] || { niiImpact: 0 });
  const adjNII = baseNII + (nearestScenario.niiImpact || 0) + (loanAdj * 1000000 * 0.058);
  const adjNIM = (adjNII / 3620000000) * 100;

  // Import missing BANK data
  const BANK_DATA = { asOfDate: "March 2, 2026", institution: "Demo Regional Bank" };

  return (
    <div>
      <PH title="ALCO Tools — Interactive Rate Sensitivity" sub="Bancware ALM · March 14, 2026 ALCO meeting prep" accentColor={CRO_COLOR} />

      {/* Rate NII Model */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 14 }}>NII Sensitivity Modeler</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>Rate Shift (bps): <strong style={{ color: CRO_COLOR }}>{rateShift >= 0 ? "+" : ""}{rateShift}</strong></div>
            <input type="range" min={-200} max={200} step={25} value={rateShift} onChange={e => setRateShift(+e.target.value)} style={{ width: "100%", accentColor: CRO_COLOR }} />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: C.dim, marginTop: 3 }}>
              <span>-200bps</span><span>Base</span><span>+200bps</span>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>Loan Growth ($M): <strong style={{ color: CRO_COLOR }}>+${loanAdj}M</strong></div>
            <input type="range" min={0} max={200} step={10} value={loanAdj} onChange={e => setLoanAdj(+e.target.value)} style={{ width: "100%", accentColor: CRO_COLOR }} />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: C.dim, marginTop: 3 }}>
              <span>$0</span><span>+$100M</span><span>+$200M</span>
            </div>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginTop: 16 }}>
          {[
            { l: "Projected NII", v: `$${(adjNII/1e6).toFixed(1)}M`, c: adjNII >= baseNII ? C.forest : C.burgundy },
            { l: "NII Δ vs Base", v: `${adjNII >= baseNII ? "+" : ""}$${((adjNII-baseNII)/1e6).toFixed(1)}M`, c: adjNII >= baseNII ? C.forest : C.burgundy },
            { l: "Projected NIM", v: `${adjNIM.toFixed(2)}%`, c: adjNIM >= 3.42 ? C.forest : C.burgundy },
          ].map((m, i) => (
            <Card key={i} style={{ padding: "12px", textAlign: "center" }}>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: .5, marginBottom: 5 }}>{m.l}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: m.c }}>{m.v}</div>
            </Card>
          ))}
        </div>
      </Card>

      {/* AFS Portfolio Summary */}
      <Card style={{ marginBottom: 16, padding: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 12 }}>AFS Portfolio — Duration & Unrealized P&L</div>
        {ip.afs.holdings.map((h, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < ip.afs.holdings.length-1 ? `1px solid ${C.border}` : "none", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: C.muted }}>{h.type}</span>
            <span style={{ fontSize: 11, color: C.dim }}>{h.dur}yr · {h.coupon.toFixed(2)}%</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>${(h.bal/1e6).toFixed(0)}M</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: h.unrealized < 0 ? C.burgundy : C.forest }}>{h.unrealized >= 0 ? "+" : ""}${(h.unrealized/1e6).toFixed(1)}M</span>
          </div>
        ))}
        <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", fontWeight: 800 }}>
          <span style={{ fontSize: 13, color: C.text }}>Total AFS Unrealized</span>
          <span style={{ fontSize: 14, color: C.burgundy }}>${(ip.afs.unrealizedLoss/1e6).toFixed(1)}M (AOCI: ${(ip.afs.aoci/1e6).toFixed(1)}M)</span>
        </div>
      </Card>

      {/* LCR Quick View */}
      <Card style={{ padding: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 12 }}>Liquidity Stress Quick View</div>
        {lm.stressOutflows.map((s, i) => (
          <div key={i} style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 12, color: C.muted }}>{s.scenario}</span>
              <span style={{ fontSize: 13, fontWeight: 800, color: s.coverage >= 1.0 ? C.forest : C.burgundy }}>LCR {(s.coverage*100).toFixed(0)}%</span>
            </div>
            <Bar val={Math.min(s.coverage * 100, 200)} max={200} color={s.coverage >= 1.0 ? C.forest : C.burgundy} />
          </div>
        ))}
      </Card>
    </div>
  );
}

// ── INTRADAY CASH POSITION ────────────────────────────────────────
export function IntradayCashPanel({ onLog }: { onLog: (a: string, d: string, c: string) => void }) {
  const ip = INTRADAY_POSITION;
  const catColors: Record<string, string> = { Opening: C.muted, Wire: C.forest, ACH: C.gold, Funding: "#1E3A5F", Exception: C.burgundy, Checks: C.rust, Current: C.burgundy };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16, color: C.text }}>Intraday Cash Position</div>
          <div style={{ fontSize: 11, color: C.muted }}>{ip.asOf}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ background: ip.surplus > 0 ? "#F0FDF4" : "#FEF2F2", border: `1px solid ${ip.surplus > 0 ? C.forest : C.burgundy}`, borderRadius: 8, padding: "8px 14px", textAlign: "center" }}>
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase" }}>Surplus</div>
            <div style={{ fontSize: 18, fontWeight: 900, color: ip.surplus > 0 ? C.forest : C.burgundy }}>${(ip.surplus/1e6).toFixed(0)}M</div>
          </div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 14px", textAlign: "center" }}>
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase" }}>Overnight Income</div>
            <div style={{ fontSize: 18, fontWeight: 900, color: C.forest }}>${ip.overnightIncome.toLocaleString()}</div>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
        {[
          ["Fed Balance", `$${(ip.fedBalance/1e6).toFixed(0)}M`, C.text],
          ["Target Balance", `$${(ip.targetBalance/1e6).toFixed(0)}M`, C.muted],
          ["Proj. EOD", `$${(ip.projectedEOD/1e6).toFixed(0)}M`, C.forest],
          ["DLO Used", `$${(ip.dayLightOverdraftUsed/1e6).toFixed(0)}M / $${(ip.dayLightOverdraftLimit/1e6).toFixed(0)}M`, ip.dayLightOverdraftUsed > 0 ? C.gold : C.forest],
        ].map(([l,v,c], i) => (
          <Card key={i} style={{ padding: "12px 14px", textAlign: "center" }}>
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: .5, marginBottom: 4 }}>{l}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: c as string }}>{v}</div>
          </Card>
        ))}
      </div>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "10px 14px", background: C.surface, fontWeight: 700, fontSize: 13, color: C.text, borderBottom: `1px solid ${C.border}` }}>Today's Activity Log</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: C.surface }}>
              {["Time","Transaction","Inflow","Outflow","Balance","Cat."].map(h => (
                <th key={h} style={{ padding: "7px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: .5 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ip.activity.map((a, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${C.border}`, background: a.cat === "Exception" ? "#FEF2F2" : a.cat === "Current" ? "#F0FDF4" : i%2===0 ? "transparent" : C.surface }}>
                <td style={{ padding: "8px 12px", color: C.muted, fontWeight: 600 }}>{a.time}</td>
                <td style={{ padding: "8px 12px", color: C.text, fontWeight: a.cat === "Current" ? 700 : 400 }}>{a.type}</td>
                <td style={{ padding: "8px 12px", color: C.forest, fontWeight: 600 }}>{a.inflow > 0 ? `+$${(a.inflow/1e6).toFixed(1)}M` : "—"}</td>
                <td style={{ padding: "8px 12px", color: C.burgundy, fontWeight: 600 }}>{a.outflow > 0 ? `-$${(a.outflow/1e6).toFixed(1)}M` : "—"}</td>
                <td style={{ padding: "8px 12px", fontWeight: 700, color: C.text }}>${(a.balance/1e6).toFixed(0)}M</td>
                <td style={{ padding: "8px 12px" }}><span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: catColors[a.cat] || C.muted, color: "#fff", fontWeight: 600 }}>{a.cat}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card style={{ padding: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: C.text, marginBottom: 10 }}>Pending Large Wire Activity</div>
        {ip.pendingWires.map((w, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < ip.pendingWires.length-1 ? `1px solid ${C.border}` : "none" }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: w.dir === "In" ? C.forest : C.burgundy, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{w.customer}</div>
              <div style={{ fontSize: 10, color: C.muted }}>{w.id} · ETA {w.eta}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: w.dir === "In" ? C.forest : C.burgundy }}>{w.dir === "In" ? "+" : "-"}${(w.amount/1e6).toFixed(1)}M</div>
              <div style={{ fontSize: 10, color: w.status.includes("OFAC") ? C.burgundy : C.muted, fontWeight: w.status.includes("OFAC") ? 700 : 400 }}>{w.status}</div>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ── VENDOR RISK PANEL ─────────────────────────────────────────────
export function VendorRiskPanel({ onLog }: { onLog: (a: string, d: string, c: string) => void }) {
  const [filter, setFilter] = useState("All");
  const tiers = ["All", "Tier 1", "Tier 2", "Tier 3"];
  const filtered = filter === "All" ? VENDOR_REGISTRY : VENDOR_REGISTRY.filter(v => v.tier === filter);
  const critical = VENDOR_REGISTRY.filter(v => v.issues).length;
  const tierColor = (t: string) => t === "Tier 1" ? C.burgundy : t === "Tier 2" ? C.gold : C.muted;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16, color: C.text }}>Third-Party Vendor Risk Registry</div>
          <div style={{ fontSize: 11, color: C.muted }}>{VENDOR_REGISTRY.length} vendors · {VENDOR_REGISTRY.filter(v=>v.tier==="Tier 1").length} critical · {critical} with open issues</div>
        </div>
        {critical > 0 && <div style={{ background: "#FEF3C7", border: `1px solid ${C.gold}`, borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 700, color: "#92400E" }}>⚠ {critical} vendors have open issues</div>}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        {tiers.map(t => (
          <button key={t} onClick={() => setFilter(t)} style={{ fontSize: 11, padding: "5px 12px", borderRadius: 20, border: `1px solid ${filter===t ? C.burgundy : C.border}`, background: filter===t ? C.burgundyBg : "transparent", color: filter===t ? C.burgundy : C.muted, cursor: "pointer", fontWeight: filter===t ? 700 : 400 }}>{t}</button>
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {filtered.map((v, i) => (
          <Card key={i} style={{ padding: "14px 16px", borderLeft: `3px solid ${v.issues ? C.gold : C.forest}` }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{v.vendor}</span>
                  <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: tierColor(v.tier), color: "#fff", fontWeight: 700 }}>{v.tier}</span>
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>{v.service} · Contract: {v.contract} · Annual: ${(v.spend/1e3).toFixed(0)}K</div>
                <div style={{ display: "flex", gap: 12, fontSize: 11 }}>
                  <span style={{ color: C.muted }}>BCP: <strong style={{color:C.text}}>{v.bcpTested}</strong></span>
                  <span style={{ color: C.muted }}>SOC 2: <strong style={{color:v.soc2.includes("Type II") ? C.forest : C.gold}}>{v.soc2}</strong></span>
                </div>
                {v.issues && <div style={{ marginTop: 8, fontSize: 11, color: C.gold, fontStyle: "italic" }}>⚠ {v.issues}</div>}
              </div>
              <div style={{ textAlign: "center", flexShrink: 0 }}>
                <div style={{ fontSize: 24, fontWeight: 900, color: v.riskScore >= 80 ? C.forest : v.riskScore >= 65 ? C.gold : C.burgundy }}>{v.riskScore}</div>
                <div style={{ fontSize: 9, color: C.dim, textTransform: "uppercase" }}>Risk Score</div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── LOAN PRICING CALCULATOR ───────────────────────────────────────
export function LoanPricingPanel({ onLog }: { onLog: (a: string, d: string, c: string) => void }) {
  const lpa = LOAN_PRICING_ASSUMPTIONS;
  const [loanType, setLoanType] = useState(lpa.loanTypes[0].type);
  const [termYrs, setTermYrs] = useState(5);
  const [riskRating, setRiskRating] = useState(4); // 1=best, 10=worst
  const [hasRelDep, setHasRelDep] = useState(true);
  const [loanAmt, setLoanAmt] = useState(2000000);

  const lt = lpa.loanTypes.find(t => t.type === loanType)!;
  const depDiscount = hasRelDep ? 0.0025 : 0;
  const ratingAdj = (riskRating - 5) * 0.0010;
  const minimumRate = (lpa.costOfFunds + lpa.servicing + lpa.origCost + lt.riskPremium + lpa.capitalCharge + ratingAdj - depDiscount) * 100;
  const competFloor = lt.competFloor;
  const recommendedRate = Math.max(minimumRate, competFloor);
  const annualRevenue = loanAmt * (recommendedRate / 100);
  const feeIncome = loanAmt * lpa.origCost;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <div style={{ fontWeight: 800, fontSize: 16, color: C.text }}>Loan Pricing Calculator</div>
        <div style={{ fontSize: 11, color: C.muted }}>Minimum acceptable rate based on fully-loaded cost of funds + risk premium</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card style={{ padding: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: C.text, marginBottom: 12 }}>Loan Parameters</div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Loan Type</label>
              <select value={loanType} onChange={e => setLoanType(e.target.value)} style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 12 }}>
                {lpa.loanTypes.map(t => <option key={t.type} value={t.type}>{t.type}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Loan Amount: ${(loanAmt/1e6).toFixed(1)}M</label>
              <input type="range" min={100000} max={10000000} step={100000} value={loanAmt} onChange={e => setLoanAmt(Number(e.target.value))} style={{ width: "100%" }} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: C.dim }}>
                <span>$100K</span><span>$10M</span>
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Term: {termYrs} year{termYrs !== 1 ? "s" : ""}</label>
              <input type="range" min={1} max={25} step={1} value={termYrs} onChange={e => setTermYrs(Number(e.target.value))} style={{ width: "100%" }} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: C.dim }}>
                <span>1yr</span><span>25yr</span>
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Risk Rating: {riskRating} / 10 (1=best)</label>
              <input type="range" min={1} max={10} step={1} value={riskRating} onChange={e => setRiskRating(Number(e.target.value))} style={{ width: "100%" }} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: C.dim }}>
                <span>1 — Excellent</span><span>10 — Criticized</span>
              </div>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, color: C.text }}>
              <input type="checkbox" checked={hasRelDep} onChange={e => setHasRelDep(e.target.checked)} />
              Relationship Deposit Account (-25bps discount)
            </label>
          </Card>
          <Card style={{ padding: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: C.muted, marginBottom: 10, textTransform: "uppercase" }}>Rate Build-Up</div>
            {[
              ["Cost of Funds",     `${(lpa.costOfFunds*100).toFixed(2)}%`,   C.muted],
              ["Servicing Cost",    `${(lpa.servicing*100).toFixed(2)}%`,      C.muted],
              ["Origination Cost",  `${(lpa.origCost*100).toFixed(2)}%`,       C.muted],
              ["Risk Premium",      `${(lt.riskPremium*100).toFixed(2)}%`,     C.gold],
              ["Capital Charge",    `${(lpa.capitalCharge*100).toFixed(2)}%`,  C.muted],
              ["Rating Adjustment", `${ratingAdj >= 0 ? "+" : ""}${(ratingAdj*100).toFixed(2)}%`, ratingAdj > 0 ? C.burgundy : C.forest],
              ["Relationship Disc.",`-${(depDiscount*100).toFixed(2)}%`,       hasRelDep ? C.forest : C.dim],
            ].map(([l,v,c],i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 11, color: C.muted }}>{l}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: c as string }}>{v}</span>
              </div>
            ))}
          </Card>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Card style={{ padding: 20, textAlign: "center", background: recommendedRate <= competFloor ? "#F0FDF4" : "#FEF3C7", border: `2px solid ${recommendedRate <= competFloor ? C.forest : C.gold}` }}>
            <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>Minimum Acceptable Rate</div>
            <div style={{ fontSize: 42, fontWeight: 900, color: C.burgundy, lineHeight: 1 }}>{minimumRate.toFixed(2)}%</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>Based on fully-loaded cost of funds + risk premium</div>
          </Card>
          <Card style={{ padding: 20, textAlign: "center" }}>
            <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>Competitor Floor</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: C.gold }}>{competFloor.toFixed(2)}%</div>
            <div style={{ fontSize: 11, color: C.muted }}>Market rate for {loanType}</div>
          </Card>
          <Card style={{ padding: 16, background: "#F0FDF4", border: `1px solid ${C.forest}` }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: C.forest, marginBottom: 10 }}>Recommended Rate: {recommendedRate.toFixed(2)}%</div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
              {recommendedRate > minimumRate
                ? `Competitor floor sets the floor — ${(recommendedRate - minimumRate).toFixed(2)}% above your cost floor.`
                : `Your cost floor exceeds competitor pricing — ensure borrower quality justifies rate.`}
            </div>
            {[
              ["Loan Amount", `$${(loanAmt/1e6).toFixed(2)}M`],
              ["Annual Interest Income", `$${(annualRevenue/1e3).toFixed(0)}K`],
              ["Origination Fee Income", `$${(feeIncome/1e3).toFixed(0)}K`],
              ["Spread Above CoF", `${(recommendedRate/100 - lpa.costOfFunds).toFixed(3)*100 < 0 ? "" : "+"}${((recommendedRate/100 - lpa.costOfFunds)*100).toFixed(2)}%`],
            ].map(([l,v], i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: i < 3 ? `1px solid ${C.border}` : "none" }}>
                <span style={{ fontSize: 11, color: C.muted }}>{l}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{v}</span>
              </div>
            ))}
          </Card>
          {lt.maxLTV && (
            <Card style={{ padding: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: C.muted, textTransform: "uppercase", marginBottom: 8 }}>Policy Limits for {loanType}</div>
              <div style={{ fontSize: 12, color: C.text }}>Max LTV: <strong>{(lt.maxLTV*100).toFixed(0)}%</strong></div>
              {lt.maxDSCR && <div style={{ fontSize: 12, color: C.text }}>Min DSCR: <strong>{lt.maxDSCR.toFixed(2)}x</strong></div>}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
